'use client';

import {
  Container,
  Flex,
  Grid,
  ScrollArea,
  Space,
  useMantineTheme,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { clsx } from 'clsx';
import { useCallback, useEffect, useRef } from 'react';

import styles from './ColumnarLayout.module.css';

interface ColumnarLayoutProps {
  columnStartIsHiddenOnMobile?: boolean;
  columnStart: React.ReactNode;
  columnEnd: React.ReactNode;
}

const ColumnarLayout: React.FC<ColumnarLayoutProps> = ({
  columnStartIsHiddenOnMobile = false,
  columnStart,
  columnEnd,
}) => {
  const theme = useMantineTheme();
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.md})`);

  const viewport = useRef<HTMLDivElement>(null);

  const scrollToBottom = useCallback(
    () =>
      viewport.current!.scrollTo({
        top: viewport.current!.scrollHeight,
        behavior: 'instant',
      }),
    [],
  );

  useEffect(() => {
    scrollToBottom();
  }, [scrollToBottom]);

  return (
    <Grid
      gutter={{ base: 0, md: 'xl' }}
      mt="lg"
      classNames={{
        inner: styles.gridInner,
      }}
    >
      {/* Startside (Left in LtR) Column */}
      <Grid.Col
        span={{ base: 12, md: 6 }}
        h="100%"
        py={0}
        visibleFrom={columnStartIsHiddenOnMobile ? 'md' : undefined}
      >
        <Flex
          justify="center"
          align="center"
          h="100%"
        >
          <Container
            size="xs"
            h="100%"
            w="100%"
          >
            <ScrollArea.Autosize
              viewportRef={viewport}
              mx="auto"
              mih={
                isMobile
                  ? 'calc(100vh - var(--app-shell-header-height, 0px) - var(--app-shell-footer-height, 0px) - 8*var(--mantine-spacing-md) - 24px)'
                  : undefined
              }
              mah={
                isMobile
                  ? '100%'
                  : 'calc(100vh - var(--app-shell-header-height, 0px) - var(--app-shell-footer-height, 0px) - 8*var(--mantine-spacing-md) - 24px)'
              }
              className={styles.scrollAreaActualRoot}
              classNames={{
                root: clsx(styles.scrollAreaDefault),
              }}
              type="never"
            >
              <Space
                h="xl"
                visibleFrom="md"
              />
              <Space
                h="xl"
                visibleFrom="md"
              />
              {columnStart}
            </ScrollArea.Autosize>
          </Container>
        </Flex>
      </Grid.Col>

      {/* Endside (Right in LtR) Column */}
      <Grid.Col
        span={{ base: 12, md: 6 }}
        h="100%"
        w="100%"
        py={0}
      >
        <Container size="xs">
          <ScrollArea.Autosize
            mx="auto"
            mah={
              isMobile
                ? '100%'
                : 'calc(100vh - var(--app-shell-header-height, 0px) - var(--app-shell-footer-height, 0px) - 6*var(--mantine-spacing-md))'
            }
            classNames={{
              root: clsx(styles.scrollAreaDefault),
            }}
            type="never"
          >
            <Space
              h="xl"
              visibleFrom="md"
            />
            <Space
              h="xl"
              visibleFrom="md"
            />
            {columnEnd}
            <Space
              visibleFrom="md"
              h="calc(100vh - var(--app-shell-header-height, 0px) - var(--app-shell-footer-height, 0px) - 8*var(--mantine-spacing-xl))"
            />
          </ScrollArea.Autosize>
        </Container>
      </Grid.Col>
    </Grid>
  );
};

export { ColumnarLayout };
